/**
 * Choose what documents are checked against: some of the organization's
 * policies, one regime it follows, or one of its templates. The choices come
 * from the organization's own library, regimes and templates; the server
 * checks every id again and takes names from there.
 *
 * @module components/apps/contractIntelligence/BasisPicker
 */
import { Link } from "@tanstack/react-router";
import { CheckIcon } from "lucide-react";
import { useMemo, useState } from "react";

import { cn } from "../../../lib/utils";
import { useCiTemplates, type CiBasis, type CiBasisKind } from "../../../state/contractIntelligence";
import { useFrameworks } from "../../../state/governance";
import { policyDisplayName, usePolicies } from "../../../state/policies";
import { SegmentedTabs } from "../../regulation/PageHeader";
import { BASIS_KIND_LABEL, readableName } from "./ciModel";
import { CiSearch } from "./ciParts";
import { BasisIcon } from "./CiReviewRow";
import { useCiOrg } from "./useCiOrg";

interface Choice {
  readonly id: string;
  readonly name: string;
  readonly detail: string | null;
}

function ChoiceList({
  choices,
  selected,
  multiple,
  onToggle,
  empty,
  label,
}: {
  readonly choices: ReadonlyArray<Choice>;
  readonly selected: ReadonlySet<string>;
  readonly multiple: boolean;
  readonly onToggle: (choice: Choice) => void;
  readonly empty: React.ReactNode;
  readonly label: string;
}) {
  if (choices.length === 0) return <div className="px-3 py-6 text-center text-xs text-muted-foreground">{empty}</div>;
  return (
    <ul role={multiple ? "group" : "radiogroup"} aria-label={label} className="max-h-72 divide-y divide-border/40 overflow-y-auto">
      {choices.map((choice) => {
        const checked = selected.has(choice.id);
        return (
          <li key={choice.id}>
            <button
              type="button"
              role={multiple ? "checkbox" : "radio"}
              aria-checked={checked}
              onClick={() => onToggle(choice)}
              className={cn(
                "flex w-full items-center gap-3 px-3 py-2 text-left outline-none transition-colors hover:bg-accent/40 focus-visible:bg-accent/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                checked && "bg-accent/30",
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "flex size-4 shrink-0 items-center justify-center border transition-colors",
                  multiple ? "rounded-[5px]" : "rounded-full",
                  checked ? "border-primary bg-primary text-primary-foreground" : "border-input bg-background",
                )}
              >
                {checked ? (multiple ? <CheckIcon className="size-3" /> : <span className="size-1.5 rounded-full bg-current" />) : null}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px]">{choice.name}</span>
                {choice.detail ? <span className="block truncate text-xs text-muted-foreground">{choice.detail}</span> : null}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export function BasisPicker({
  organizationId,
  value,
  onChange,
  compact = false,
}: {
  readonly organizationId: string;
  readonly value: CiBasis | null;
  readonly onChange: (basis: CiBasis | null) => void;
  readonly compact?: boolean;
}) {
  const { orgSegment } = useCiOrg();
  const [kind, setKind] = useState<CiBasisKind>(value?.kind ?? "policies");
  const [query, setQuery] = useState("");
  const policies = usePolicies(organizationId);
  const frameworks = useFrameworks(organizationId);
  const templates = useCiTemplates(organizationId);

  const choices = useMemo((): { list: ReadonlyArray<Choice>; loading: boolean; failed: boolean } => {
    if (kind === "policies") {
      const jurisdictionName = new Map((policies.snapshot?.jurisdictions ?? []).map((entry) => [entry.slug, entry.name]));
      const list = (policies.snapshot?.policies ?? [])
        .filter((policy) => policy.status === "ready" && policy.reference !== true)
        .map((policy) => ({
          id: policy.id,
          name: readableName(policyDisplayName(policy)),
          detail: [
            policy.documentKind === "procedure" ? "Procedure" : "Policy",
            ...policy.jurisdictionSlugs.map((slug) => jurisdictionName.get(slug) ?? slug),
          ].join(" · "),
        }));
      return { list, loading: policies.snapshot === null && policies.unavailableReason === null, failed: policies.unavailableReason !== null };
    }
    if (kind === "regime") {
      const list = (frameworks.snapshot?.frameworks ?? [])
        .filter((framework) => framework.enabled)
        .map((framework) => ({ id: framework.id, name: framework.name, detail: [framework.issuer, framework.jurisdiction].filter(Boolean).join(" · ") || null }));
      return { list, loading: frameworks.snapshot === null && frameworks.unavailableReason === null, failed: frameworks.unavailableReason !== null };
    }
    const read = templates.read;
    const list =
      read?.kind === "ready"
        ? read.value.map((template) => ({
            id: template.id,
            name: template.name,
            detail: [template.documentType, `${template.items.length} requirement${template.items.length === 1 ? "" : "s"}`].filter(Boolean).join(" · "),
          }))
        : [];
    return { list, loading: read === null, failed: read !== null && read.kind !== "ready" };
  }, [frameworks.snapshot, frameworks.unavailableReason, kind, policies.snapshot, policies.unavailableReason, templates.read]);

  const needle = query.trim().toLowerCase();
  const shown = needle ? choices.list.filter((choice) => `${choice.name} ${choice.detail ?? ""}`.toLowerCase().includes(needle)) : choices.list;
  const selected = new Set(value && value.kind === kind ? value.items.map((item) => item.id) : []);

  const toggle = (choice: Choice) => {
    if (kind !== "policies") {
      onChange({ kind, items: [{ id: choice.id, name: choice.name }] });
      return;
    }
    const current = value && value.kind === "policies" ? value.items : [];
    const next = selected.has(choice.id)
      ? current.filter((item) => item.id !== choice.id)
      : [...current, { id: choice.id, name: choice.name }];
    onChange(next.length > 0 ? { kind: "policies", items: next } : null);
  };

  const empty =
    kind === "policies" ? (
      "Your library has no processed policies yet."
    ) : kind === "regime" ? (
      "Your organization follows no regimes yet. Add one in Governance."
    ) : (
      <>
        No templates yet.{" "}
        {orgSegment ? (
          <Link to="/$org/apps/contract-intelligence/templates" params={{ org: orgSegment }} className="underline underline-offset-2 hover:text-foreground">
            Add one
          </Link>
        ) : null}
      </>
    );

  return (
    <div className="space-y-2" data-ci-basis-picker="">
      <SegmentedTabs<CiBasisKind>
        label="Check against"
        value={kind}
        onValueChange={(next) => {
          setKind(next);
          setQuery("");
          if (value && value.kind !== next) onChange(null);
        }}
        tabs={(["policies", "regime", "template"] as const).map((entry) => ({ value: entry, label: BASIS_KIND_LABEL[entry] }))}
      />
      <div className="overflow-hidden rounded-xl border border-border/70 bg-card">
        {choices.list.length > 6 ? (
          <div className="border-b border-border/50 p-2">
            <CiSearch value={query} onChange={setQuery} label={`Search ${BASIS_KIND_LABEL[kind].toLowerCase()}`} placeholder={`Search ${BASIS_KIND_LABEL[kind].toLowerCase()}`} className="sm:w-full" />
          </div>
        ) : null}
        {choices.loading ? (
          <div className="space-y-2 p-3" aria-busy="true">
            {[0, 1, 2].map((index) => (
              <div key={index} className="h-8 animate-pulse rounded-md bg-muted/40" />
            ))}
          </div>
        ) : choices.failed ? (
          <div className="px-3 py-6 text-center text-xs text-muted-foreground">These could not be loaded. Try again in a moment.</div>
        ) : (
          <ChoiceList
            choices={shown}
            selected={selected}
            multiple={kind === "policies"}
            onToggle={toggle}
            empty={needle ? "Nothing matches." : empty}
            label={BASIS_KIND_LABEL[kind]}
          />
        )}
        {kind === "policies" && selected.size > 0 && !compact ? (
          <div className="flex items-center gap-2 border-t border-border/50 px-3 py-2 text-xs text-muted-foreground">
            <BasisIcon kind="policies" />
            {selected.size} selected
            <span className="flex-1" />
            <button type="button" className="rounded px-1 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring outline-none" onClick={() => onChange(null)}>
              Clear
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
